import ArrayUtils from "../utils/ArrayUtils.js";
import StorageService from "../services/StorageService.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { bindAnswerKeys } from "../utils/QuizKeyboard.js";
import BaseGame from "./BaseGame.js";
import { zoneCountries } from "../utils/ZoneScope.js";

/**
 * Base class for the lives-based, 4-choice multiple-choice games
 * (Flag Nerd Classic, Capital Nerd Classic, Capital Nerd Hard).
 *
 * It owns the shared proposal generation (with reload-resume) and the click
 * handling. Subclasses customise the question/answers via the hooks:
 * `getDistractorPool`, `buildProposalEntry`, `renderQuestion`, `getAnswerLabel`,
 * `buildError` (and `renderErrorItem` from BaseGame).
 */
export default class BaseQuizGame extends BaseGame {
    newProposal() {
        if (this.countriesLeft.length === 0) {
            this.finishGame();
            return;
        }

        let refreshDetected = false;
        let current = StorageService.load(`${this.gameId}.current`);

        if (!current || current.found) {
            this.rightAnswer = this.drawQuestion();
            StorageService.save(`${this.gameId}.current`, this.rightAnswer);
        } else {
            this.rightAnswer = current;
            refreshDetected = true;
        }

        let proposals = [];
        if (refreshDetected) {
            proposals = StorageService.load(`${this.gameId}.proposals`);
        } else {
            proposals.push(this.buildProposalEntry(this.rightAnswer));
            const pool = this.getDistractorPool();
            const maxLength = Math.min(pool.length, 4);
            // Deux propositions au même libellé rendraient la question injouable :
            // on les écarte. Le plafond d'essais protège d'un pool trop pauvre en
            // libellés distincts (une petite zone, par exemple).
            const label = (entry) => this.getAnswerLabel(entry);

            for (let attempts = 0; proposals.length < maxLength && attempts < 500; attempts++) {
                const proposal = pool[Math.floor(Math.random() * pool.length)];
                const entry = this.buildProposalEntry(proposal);
                if (proposal.code !== this.rightAnswer.code
                    && !proposals.find(p => p.code === proposal.code || label(p) === label(entry))) {
                    proposals.push(entry);
                }
            }
            ArrayUtils.shuffle(proposals);
            StorageService.save(`${this.gameId}.proposals`, proposals);
        }

        this.whenFlagsReady(this.getQuestionFlagCodes(this.rightAnswer), () => {
            this.flagContainer.innerHTML = this.renderQuestion(this.rightAnswer);
            this.answerContainer.innerHTML = "";
            proposals.forEach(proposal => {
                this.answerContainer.insertAdjacentHTML("beforeend", `<button type="button" class="country" data-country-code="${proposal.code}">${this.getAnswerLabel(proposal)}</button>`);
            });

            this.bindGuessEvents();
        }, { clearAnswers: true });
    }

    bindGuessEvents() {
        this.answerContainer.style.pointerEvents = "initial";
        this.canGuess = true;
        // Une seule fois pour la partie : le raccourci lit les boutons au moment de
        // la frappe, il survit donc au remplacement des propositions à chaque tour.
        if (!this.unbindAnswerKeys) this.unbindAnswerKeys = bindAnswerKeys(this.answerContainer);
        this.page.querySelectorAll(".country").forEach(guessBtn => {
            guessBtn.addEventListener("click", () => {
                // `pointer-events: none` only stops the mouse: the answered buttons
                // stay in the DOM (and keep their listeners) for the ~1.1s of
                // `advanceTurn()`, and a focused button still fires `click` on
                // Enter/Space. Without this flag a keyboard player could process the
                // same turn twice — double life penalty, duplicated error entry.
                if (!this.canGuess) return;
                this.canGuess = false;
                this.answerContainer.style.pointerEvents = "none";
                const isCorrect = guessBtn.dataset.countryCode === this.rightAnswer.code;

                if (isCorrect) {
                    SoundManager.correct();
                    FeedbackEffects.confetti(guessBtn);
                    guessBtn.classList.add("valid");
                    this.markFound();
                } else {
                    SoundManager.wrong();
                    FeedbackEffects.shake(this.page.querySelector(".game-container"));
                    this.page.querySelector(`[data-country-code="${this.rightAnswer.code}"]`).classList.add("valid");
                    guessBtn.classList.add("invalid");
                    this.markWrong(this.buildError(guessBtn));
                }

                this.advanceTurn();
            });
        });
    }

    // --- Hooks -------------------------------------------------------------

    /** Pool the distractors are drawn from: the full country set, or the zone's countries (ZoneScope). */
    getDistractorPool() {
        return zoneCountries(geoNerdApp.countries, this.zone);
    }

    /** Shape of a stored proposal entry. */
    buildProposalEntry(country) {
        return { code: country.code, name: country.name };
    }

    /** Drapeaux à avoir en cache avant d'afficher la question (aucun si elle n'en montre pas). */
    getQuestionFlagCodes(country) {
        return [country.code];
    }

    /** HTML rendered in the flag/question container. */
    renderQuestion(country) {
        return `<div class="flag" style="background-image: url(${getFlagPath(country.code)})"></div>`;
    }

    /** Text shown on each answer button. */
    getAnswerLabel(proposal) {
        return proposal.name;
    }

    /** Error entry pushed on a wrong answer (consumed by `renderErrorItem`). */
    buildError(guessBtn) {
        return {
            countryName: this.rightAnswer.name,
            correctCode: this.rightAnswer.code,
            guessedCode: guessBtn.dataset.countryCode
        };
    }
}
